import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles, Contrast, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLocale } from '@/contexts/LocaleContext';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

type Suggestion = { id: string; title: string };

const HC_KEY = 'home-search-hc';

export function HomeSearch() {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const dir = isArabic ? 'rtl' : 'ltr';
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>('');
  const [debounced, setDebounced] = useState<string>('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [highContrast, setHighContrast] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(HC_KEY) === '1';
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = 'home-search-suggestions';

  // Persist HC mode
  useEffect(() => {
    try {
      window.localStorage.setItem(HC_KEY, highContrast ? '1' : '0');
    } catch {}
  }, [highContrast]);

  // Debounce input (180ms) to limit DB hits on fast typing.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 180);
    return () => window.clearTimeout(t);
  }, [query]);

  // Progressive suggestions from activities. Aborts on stale queries.
  useEffect(() => {
    if (debounced.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const field = isArabic ? 'title_ar' : 'title_en';
    supabase
      .from('activities')
      .select(`id, title_ar, title_en`)
      .eq('is_active', true)
      .ilike(field, `%${debounced}%`)
      .limit(6)
      .then(({ data }) => {
        if (cancelled) return;
        const items: Suggestion[] = (data || []).map((a: any) => ({
          id: a.id,
          title: (isArabic ? a.title_ar : a.title_en) || a.title_ar || a.title_en || '',
        }));
        setSuggestions(items);
        setActiveIndex(-1);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, isArabic]);

  const openAssistant = (q?: string) => {
    const text = (q ?? query).trim();
    navigate(text ? `/ai-search?q=${encodeURIComponent(text)}` : '/ai-search');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeIndex >= 0 && suggestions[activeIndex]) {
      navigate(`/activity/${suggestions[activeIndex].id}`);
      return;
    }
    openAssistant();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    } else if (e.key === 'Home') {
      setActiveIndex(0);
    } else if (e.key === 'End') {
      setActiveIndex(suggestions.length - 1);
    }
  };

  const showList = open && (loading || suggestions.length > 0);

  const containerCls = useMemo(
    () =>
      cn(
        'relative flex items-center rounded-full pe-1 ps-5 py-1 transition-all',
        highContrast
          ? 'bg-white border-2 border-foreground ring-1 ring-foreground/20 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.35)] focus-within:ring-4 focus-within:ring-primary'
          : 'bg-white/60 backdrop-blur-2xl backdrop-saturate-200 border border-white/80 ring-1 ring-white/70 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.28)] focus-within:bg-white/80 focus-within:ring-2 focus-within:ring-primary/60'
      ),
    [highContrast]
  );

  return (
    <form
      onSubmit={handleSubmit}
      dir={dir}
      className="w-full max-w-2xl mx-auto relative"
      role="search"
    >
      <div className={containerCls}>
        <Sparkles className="w-4 h-4 text-primary shrink-0 me-2 drop-shadow" aria-hidden="true" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          dir={dir}
          placeholder={isArabic ? 'إسئلني شنو بخاطرك...' : 'Ask me anything...'}
          aria-label={isArabic ? 'البحث' : 'Search'}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
          }
          className={cn(
            'flex-1 border-0 bg-transparent shadow-none h-12 px-0 text-base',
            'focus-visible:ring-0 focus-visible:ring-offset-0',
            isArabic ? 'text-start' : 'text-start',
            highContrast
              ? 'text-foreground placeholder:text-foreground/80'
              : 'text-foreground placeholder:text-foreground/70'
          )}
        />

        {/* High contrast toggle removed */}


        <Button
          type="submit"
          size="icon"
          className="rounded-full bg-white/30 backdrop-blur-xl text-foreground border border-white/60 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.2),inset_0_1px_0_0_rgba(255,255,255,0.6)] hover:bg-white/45 h-11 w-11 shrink-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-label={isArabic ? 'افتح المساعد الذكي' : 'Open AI assistant'}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <Search className="w-4 h-4" aria-hidden="true" />
          )}
        </Button>
      </div>

      {showList && (
        <ul
          id={listboxId}
          role="listbox"
          dir={dir}
          className={cn(
            'absolute z-50 mt-2 w-full rounded-2xl overflow-hidden',
            highContrast
              ? 'bg-white border-2 border-foreground shadow-xl'
              : 'bg-white/85 backdrop-blur-xl border border-white/70 ring-1 ring-black/5 shadow-xl'
          )}
        >
          {loading && suggestions.length === 0 && (
            <li className="px-4 py-3 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              {isArabic ? 'جاري البحث...' : 'Searching...'}
            </li>
          )}
          {suggestions.map((s, i) => (
            <li
              id={`${listboxId}-opt-${i}`}
              key={s.id}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                navigate(`/activity/${s.id}`);
              }}
              onMouseEnter={() => setActiveIndex(i)}
              className={cn(
                'px-4 py-2.5 text-sm cursor-pointer truncate',
                isArabic ? 'text-start' : 'text-start',
                i === activeIndex
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              )}
            >
              {s.title}
            </li>
          ))}
          {!loading && suggestions.length > 0 && (
            <li
              role="option"
              aria-selected={false}
              onMouseDown={(e) => {
                e.preventDefault();
                openAssistant();
              }}
              className="px-4 py-2.5 text-sm cursor-pointer border-t border-border/60 text-primary hover:bg-muted flex items-center gap-2"
            >
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              {isArabic ? `اسأل المساعد عن "${query}"` : `Ask assistant about "${query}"`}
            </li>
          )}
        </ul>
      )}
    </form>
  );
}
